/**
 * Shared Review & Confirm panel for receipt (and future vision) pantry adds.
 * Nothing writes until the user confirms checked candidates.
 */
import { useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState, forwardRef } from 'react'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '../../design-system/primitives/index.js'
import PantryAddFeedbackBanner from './PantryAddFeedbackBanner.jsx'
import PantryCandidateRow from './PantryCandidateRow.jsx'
import './PantryReviewConfirm.css'

function normalizeCandidate(raw) {
  return {
    id: String(raw.id),
    name: raw.name || '',
    checked: raw.confidence === 'high',
    confidence: raw.confidence === 'low' ? 'low' : 'high',
    suggestion: raw.suggestion || null,
    category: raw.category || null,
    category_token: raw.category_token || null,
    quantity: raw.quantity ?? null,
    quantity_unit: raw.quantity_unit || null,
    quantity_display: raw.quantity_display || null,
    measure_display: raw.measure_display || null,
  }
}

/**
 * @param {{
 *   source?: 'receipt' | 'vision',
 *   backLabel?: string,
 *   sourcePreview?: import('react').ReactNode,
 *   candidates: object[],
 *   unrecognized?: Array<{ item_name?: string, reason?: string }>,
 *   onBack: () => void,
 *   onClose: () => void,
 *   onCommit: (checked: object[]) => void | Promise<void>,
 *   onEmptyReturn?: () => void,
 *   committing?: boolean,
 *   commitError?: string | null,
 *   onClearCommitError?: () => void,
 *   committed?: { added?: number, failed?: number } | null,
 * }} props
 */
const PantryReviewConfirm = forwardRef(function PantryReviewConfirm(
  {
    source = 'receipt',
    backLabel = '',
    sourcePreview = null,
    candidates = [],
    unrecognized = [],
    onBack,
    onClose,
    onCommit,
    onEmptyReturn,
    committing = false,
    commitError = null,
    onClearCommitError,
    committed = null,
  },
  ref,
) {
  const [items, setItems] = useState(() => candidates.map(normalizeCandidate))
  const [editingId, setEditingId] = useState(null)
  const [unrecOpen, setUnrecOpen] = useState(false)
  const [discardOpen, setDiscardOpen] = useState(false)
  const [discardAction, setDiscardAction] = useState('back')
  const hadItemsRef = useRef(candidates.length > 0)
  const titleId = useId()
  const descId = useId()

  useEffect(() => {
    setItems(candidates.map(normalizeCandidate))
    hadItemsRef.current = candidates.length > 0
  }, [candidates])

  useEffect(() => {
    if (hadItemsRef.current && items.length === 0 && onEmptyReturn) {
      onEmptyReturn()
    }
  }, [items.length, onEmptyReturn])

  const high = useMemo(() => items.filter((i) => i.confidence === 'high'), [items])
  const low = useMemo(() => items.filter((i) => i.confidence === 'low'), [items])
  const checkedCount = useMemo(() => items.filter((i) => i.checked).length, [items])

  const toggle = useCallback((id) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, checked: !i.checked } : i)))
    onClearCommitError?.()
  }, [onClearCommitError])

  const startEdit = useCallback((id) => setEditingId(id), [])
  const cancelEdit = useCallback(() => setEditingId(null), [])

  const commitEdit = useCallback((id, name) => {
    setItems((prev) =>
      prev.map((i) =>
        i.id === id ? { ...i, name, confidence: 'high', suggestion: null } : i,
      ),
    )
    setEditingId(null)
    onClearCommitError?.()
  }, [onClearCommitError])

  const discard = useCallback((id) => {
    setItems((prev) => prev.filter((i) => i.id !== id))
    if (editingId === id) setEditingId(null)
    onClearCommitError?.()
  }, [editingId, onClearCommitError])

  const applySuggestion = useCallback((id, suggestion) => {
    setItems((prev) =>
      prev.map((i) =>
        i.id === id
          ? {
              ...i,
              name: suggestion,
              confidence: 'high',
              suggestion: null,
              checked: true,
              // Keep extracted weight/size (e.g. 0.5 lb) when accepting a name hint.
              quantity: i.quantity,
              quantity_unit: i.quantity_unit,
              quantity_display: i.quantity_display,
              measure_display: i.measure_display,
            }
          : i,
      ),
    )
    onClearCommitError?.()
  }, [onClearCommitError])

  const requestDiscard = useCallback(
    (action) => {
      if (checkedCount > 0) {
        setDiscardAction(action)
        setDiscardOpen(true)
        return
      }
      if (action === 'back') onBack()
      else onClose()
    },
    [checkedCount, onBack, onClose],
  )

  const confirmDiscard = useCallback(() => {
    setDiscardOpen(false)
    if (discardAction === 'back') onBack()
    else onClose()
  }, [discardAction, onBack, onClose])

  useImperativeHandle(
    ref,
    () => ({
      requestClose: () => requestDiscard('close'),
      isReviewing: true,
      hasChecked: checkedCount > 0,
    }),
    [requestDiscard, checkedCount],
  )

  if (committed) {
    const added = committed.added ?? 0
    const failed = committed.failed ?? 0
    return (
      <div className="pantry-review-confirm">
        <PantryAddFeedbackBanner
          lastResult={{
            added,
            updated: 0,
            failed,
            not_imported: [],
          }}
        />
      </div>
    )
  }

  return (
    <div className="pantry-review-confirm">
      <div className="pantry-review-confirm__header">
        <button type="button" className="pantry-review-confirm__back" onClick={onBack}>
          ← {backLabel || (source === 'receipt' ? 'Edit pasted text' : 'Edit photo')}
        </button>
        <div className="pantry-review-confirm__header-spacer" />
        {sourcePreview}
        <button
          type="button"
          className="pantry-review-confirm__close"
          onClick={() => requestDiscard('close')}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div className="pantry-review-confirm__intro">
        <div className="pantry-review-confirm__title">We think we found these — tap to confirm</div>
        <div className="pantry-review-confirm__sub">
          Checked items go into your pantry when you tap Add items.
        </div>
      </div>

      {high.length > 0 ? (
        <>
          <div className="pantry-review-group-header pantry-review-group-header--ok">
            <span className="pantry-review-group-header__label">Ready to add</span>
            <span className="pantry-review-group-header__count">({high.length})</span>
          </div>
          {high.map((item) => (
            <PantryCandidateRow
              key={item.id}
              item={item}
              editingId={editingId}
              onToggle={toggle}
              onStartEdit={startEdit}
              onCommitEdit={commitEdit}
              onCancelEdit={cancelEdit}
              onDiscard={discard}
              onApplySuggestion={applySuggestion}
            />
          ))}
        </>
      ) : null}

      {low.length > 0 ? (
        <>
          <div className="pantry-review-group-header pantry-review-group-header--warn">
            <span className="pantry-review-group-header__label">Needs a look</span>
            <span className="pantry-review-group-header__count">({low.length})</span>
          </div>
          {low.map((item) => (
            <PantryCandidateRow
              key={item.id}
              item={item}
              editingId={editingId}
              onToggle={toggle}
              onStartEdit={startEdit}
              onCommitEdit={commitEdit}
              onCancelEdit={cancelEdit}
              onDiscard={discard}
              onApplySuggestion={applySuggestion}
            />
          ))}
        </>
      ) : null}

      {unrecognized.length > 0 ? (
        <div className="pantry-review-unrecognized">
          <button
            type="button"
            className="pantry-review-unrecognized__toggle"
            onClick={() => setUnrecOpen((v) => !v)}
            aria-expanded={unrecOpen}
          >
            {unrecOpen ? '▾' : '▸'} {unrecognized.length} line
            {unrecognized.length !== 1 ? 's' : ''} we couldn&apos;t read — won&apos;t be added
          </button>
          {unrecOpen ? (
            <ul className="pantry-review-unrecognized__list">
              {unrecognized.map((u, i) => (
                <li key={i} className="pantry-review-unrecognized__row">
                  <span>{u.item_name || u.name}</span>
                  <span className="pantry-add-feedback__reason-tag">
                    {u.reason === 'non-food'
                      ? 'Non-food item'
                      : u.reason === 'unrecognized'
                        ? 'Unrecognized'
                        : u.reason || 'Unrecognized'}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {commitError ? (
        <div className="pantry-review-confirm__commit-error">
          <PantryAddFeedbackBanner
            lastResult={{ error: commitError, errorTitle: "Couldn't add these items" }}
            onTryManual={() => onCommit(items.filter((i) => i.checked))}
          />
        </div>
      ) : null}

      <div className="pantry-review-confirm__footer">
        <Button
          type="button"
          variant="primary"
          disabled={checkedCount === 0 || committing}
          onClick={() => onCommit(items.filter((i) => i.checked))}
        >
          {committing ? 'Adding…' : `Add ${checkedCount} item${checkedCount !== 1 ? 's' : ''}`}
        </Button>
        <Button type="button" variant="ghost" onClick={() => requestDiscard('back')} disabled={committing}>
          Cancel
        </Button>
      </div>

      <Modal
        open={discardOpen}
        onClose={() => setDiscardOpen(false)}
        titleId={titleId}
        describedById={descId}
        overlayClassName="shop-save-dialog-overlay"
        contentClassName="pantry-confirm-panel"
      >
        <ModalHeader>
          <h2 id={titleId} className="pantry-confirm-title">
            Discard this review?
          </h2>
        </ModalHeader>
        <ModalBody>
          <p id={descId} className="pantry-confirm-description">
            You&apos;ll lose {checkedCount} reviewed item{checkedCount !== 1 ? 's' : ''}. Nothing has
            been added to your pantry yet.
          </p>
        </ModalBody>
        <ModalFooter className="pantry-confirm-actions">
          <Button type="button" variant="secondary" onClick={() => setDiscardOpen(false)}>
            Keep reviewing
          </Button>
          <Button type="button" variant="primary" className="pantry-review-discard-confirm" onClick={confirmDiscard}>
            Discard
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
})

export default PantryReviewConfirm
